import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Download, Calendar, ArrowLeft, Eye, X, Wallet, Filter, FileImage, Image } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuthHeader() {
  const token = localStorage.getItem("user_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function UserReports() {
  const navigate = useNavigate();
  const [reports, setReports] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [viewingDetail, setViewingDetail] = useState(null);
  const [showMode, setShowMode] = useState("all"); // "all" = Tagihan + Admin, "tagihan_only" = Tagihan Saja
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [viewingProof, setViewingProof] = useState(null);
  const [activeTab, setActiveTab] = useState("digiflazz"); // "digiflazz" | "lainnya"
  
  const userName = localStorage.getItem("user_name") || "";
  const userRole = localStorage.getItem("user_role") || "";
  const isLoggedIn = !!localStorage.getItem("user_token");

  useEffect(() => {
    if (!isLoggedIn) {
      toast.error("Silakan login terlebih dahulu");
      navigate("/login");
      return;
    }
    fetchReports();
  }, [isLoggedIn, navigate]);

  const fetchReports = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      
      const response = await axios.get(`${API}/user/reports?${params.toString()}`, {
        headers: getAuthHeader()
      });
      setReports(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        // Do NOT auto-clear tokens or redirect. Users only log out via "Keluar" button.
        toast.error("Gagal memuat laporan (unauthorized).");
      } else {
        toast.error("Gagal memuat laporan");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateExcel = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "2-digit", 
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getServiceLabel = (type) => {
    const labels = {
      pdam: "PDAM",
      pdam_tebo: "PDAM Tebo",
      token_listrik: "Token Listrik",
      tagihan_listrik: "Tagihan Listrik",
      pulsa: "Pulsa"
    };
    return labels[type] || type;
  };

  // --- Tab separation: DigiFlazz vs Lainnya -------------------------------
  const monthCountTxn = (t) => {
    if (t.service_type !== 'pdam' && t.service_type !== 'pdam_tebo') return 0;
    if (t.jumlah_bulan && t.jumlah_bulan > 0) return t.jumlah_bulan;
    const pl = t.periode_list || "";
    const m = pl.split(',').filter((s) => s.trim()).length;
    return m || 1;
  };
  const allTxns = (reports && reports.transactions) || [];
  const displayTxns = allTxns.filter((t) =>
    activeTab === "digiflazz" ? t.is_digiflazz : !t.is_digiflazz
  );
  const computeTabSummary = (txns) => {
    const tagihan = txns.reduce((s, t) => s + (t.amount || t.jumlah || 0), 0);
    const admin = txns.reduce((s, t) => s + (t.admin_fee || 0), 0);
    const bulan = txns.reduce((s, t) => s + monthCountTxn(t), 0);
    return {
      total_transactions: txns.length,
      total_amount: tagihan + admin,
      total_tagihan: tagihan,
      total_admin_fee: admin,
      total_petugas: bulan * 1000,
      total_sistem: bulan * 1000,
      total_maintenance: bulan * 500,
    };
  };
  const displaySummary = reports
    ? (allTxns.length ? computeTabSummary(displayTxns) : reports.summary)
    : null;

  // Calculate total tagihan (tanpa admin fee)
  const getTotalTagihan = () => {
    if (!reports || !displayTxns) return 0;
    return displayTxns.reduce((sum, t) => sum + (t.amount || t.jumlah || 0), 0);
  };

  // Calculate total pendapatan (tagihan + admin)
  const getTotalPendapatan = () => {
    return getTotalTagihan() + (displaySummary?.total_admin_fee || 0);
  };

  // Count months helper
  const countMonths = (periodeStr, serviceType) => {
    if (serviceType !== 'pdam' && serviceType !== 'pdam_tebo') return '-';
    if (!periodeStr) return 1;
    const months = periodeStr.split(',').filter(m => m.trim()).length;
    return months || 1;
  };

  const exportExcel = () => {
    if (!reports || !displayTxns.length) {
      toast.error("Tidak ada data untuk di-export");
      return;
    }

    const formatRupiah = (amount) => `Rp ${new Intl.NumberFormat("id-ID").format(amount || 0)}`;
    const isTagihanOnly = showMode === "tagihan_only";
    const modeLabel = isTagihanOnly ? " (Tagihan Saja)" : "";

    // Header row based on mode
    const headerRow = isTagihanOnly 
      ? ["No", "Layanan", "No. Pelanggan", "Bulan", "Nama", "Tagihan", "Metode", "Tanggal", "Status"]
      : ["No", "Layanan", "No. Pelanggan", "Bulan", "Nama", "Tagihan", "Biaya Admin", "Total Bayar", "Metode", "Tanggal", "Status"];

    const wsData = [
      [`LAPORAN TRANSAKSI - ${userName} (${userRole})${modeLabel}`],
      [`Periode: ${startDate || 'Semua'} s/d ${endDate || 'Semua'}`],
      [`Tanggal Export: ${new Date().toLocaleString("id-ID")}`],
      [],
      headerRow,
    ];

    displayTxns.forEach((t, i) => {
      const baseRow = [
        i + 1,
        getServiceLabel(t.service_type),
        t.customer_no || t.no_samb || "",
        countMonths(t.periode_list, t.service_type),
        t.customer_name || t.nama || "",
        formatRupiah(t.amount || t.jumlah),
      ];

      if (isTagihanOnly) {
        wsData.push([
          ...baseRow,
          t.payment_method?.toLowerCase() === 'cash' ? 'Cash' : t.payment_method?.toLowerCase() === 'transfer' ? 'Transfer' : '-',
          formatDateExcel(t.created_at),
          t.status === 'completed' || t.status === 'confirmed' || t.status === 'success' ? 'Sukses' : t.status
        ]);
      } else {
        wsData.push([
          ...baseRow,
          formatRupiah(t.admin_fee),
          formatRupiah(t.total || t.total_bayar),
          t.payment_method?.toLowerCase() === 'cash' ? 'Cash' : t.payment_method?.toLowerCase() === 'transfer' ? 'Transfer' : '-',
          formatDateExcel(t.created_at),
          t.status === 'completed' || t.status === 'confirmed' || t.status === 'success' ? 'Sukses' : t.status
        ]);
      }
    });

    // Add total row
    wsData.push([]);
    if (isTagihanOnly) {
      wsData.push(["", "", "", "", "TOTAL:", formatRupiah(getTotalTagihan()), "", ""]);
    } else {
      wsData.push(["", "", "", "", "TOTAL:", 
        formatRupiah(getTotalTagihan()),
        formatRupiah(displaySummary.total_admin_fee),
        formatRupiah(getTotalPendapatan()),
        "", ""
      ]);
    }

    // Add summary
    wsData.push([]);
    wsData.push(["RINGKASAN"]);
    wsData.push(["Total Transaksi:", displaySummary.total_transactions]);
    wsData.push(["Total Tagihan:", formatRupiah(getTotalTagihan())]);
    if (!isTagihanOnly) {
      wsData.push(["Total Biaya Admin:", formatRupiah(displaySummary.total_admin_fee)]);
      wsData.push(["Total Pendapatan:", formatRupiah(getTotalPendapatan())]);
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    // Set column widths based on mode
    ws['!cols'] = isTagihanOnly 
      ? [
          { wch: 5 }, { wch: 14 }, { wch: 16 }, { wch: 8 }, { wch: 25 },
          { wch: 18 }, { wch: 18 }, { wch: 10 }
        ]
      : [
          { wch: 5 }, { wch: 14 }, { wch: 16 }, { wch: 8 }, { wch: 25 },
          { wch: 18 }, { wch: 15 }, { wch: 18 }, { wch: 18 }, { wch: 10 }
        ];

    XLSX.utils.book_append_sheet(wb, ws, "Laporan Transaksi");
    const modeFileSuffix = isTagihanOnly ? '_tagihan_saja' : '';
    XLSX.writeFile(wb, `laporan_${userRole}_${new Date().toISOString().split("T")[0]}${modeFileSuffix}.xlsx`);
    toast.success("File Excel berhasil di-download");
  };

  // Export PDF function (similar to AdminReports)
  const exportPDF = async () => {
    if (!reports || !displayTxns.length) {
      toast.error("Tidak ada data untuk di-export");
      return;
    }

    setIsExportingPDF(true);
    const isTagihanOnly = showMode === "tagihan_only";
    const modeLabel = isTagihanOnly ? " (Tagihan Saja)" : "";
    toast.loading(`Memproses export PDF${modeLabel}...`, { id: "pdf-export" });

    try {
      const ROWS_PER_PAGE = 25;
      const transactions = displayTxns;
      const totalPages = Math.ceil(transactions.length / ROWS_PER_PAGE);
      
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const formatRp = (amount) => `Rp ${new Intl.NumberFormat("id-ID").format(amount || 0)}`;
      
      const formatTgl = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString("id-ID", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric"
        });
      };

      for (let page = 0; page < totalPages; page++) {
        if (page > 0) {
          pdf.addPage();
        }

        const startIdx = page * ROWS_PER_PAGE;
        const endIdx = Math.min(startIdx + ROWS_PER_PAGE, transactions.length);
        const pageTransactions = transactions.slice(startIdx, endIdx);

        const container = document.createElement('div');
        container.style.cssText = `
          position: fixed;
          left: -9999px;
          top: 0;
          width: 1400px;
          padding: 24px;
          background: white;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        `;

        // Build page header
        const headerHtml = `
          <div style="margin-bottom: 16px; display: flex; justify-content: space-between; align-items: center;">
            <div>
              <h1 style="font-size: 20px; font-weight: 700; color: #0ea5e9; margin: 0;">
                LAPORAN TRANSAKSI - ${userName}${isTagihanOnly ? ' <span style="font-size: 14px; background: #fed7aa; color: #c2410c; padding: 2px 10px; border-radius: 9999px; margin-left: 8px;">Tagihan Saja</span>' : ''}
              </h1>
              <p style="font-size: 12px; color: #64748b; margin: 4px 0 0 0;">
                Role: ${userRole} | Periode: ${startDate || 'Semua'} s/d ${endDate || 'Semua'} | Export: ${new Date().toLocaleDateString('id-ID')}
              </p>
            </div>
            <div style="text-align: right;">
              <span style="font-size: 14px; color: #64748b;">Halaman ${page + 1} dari ${totalPages}</span>
            </div>
          </div>
        `;

        // Build table rows
        let tableRows = '';
        pageTransactions.forEach((t, idx) => {
          const rowNum = startIdx + idx + 1;
          const serviceLabel = getServiceLabel(t.service_type);
          const serviceBg = t.service_type === 'pdam' ? '#dbeafe' :
                           t.service_type === 'pdam_tebo' ? '#cffafe' :
                           t.service_type === 'token_listrik' ? '#fef3c7' :
                           t.service_type === 'pulsa' ? '#f3e8ff' : '#f1f5f9';
          const serviceColor = t.service_type === 'pdam' ? '#1d4ed8' :
                              t.service_type === 'pdam_tebo' ? '#0891b2' :
                              t.service_type === 'token_listrik' ? '#d97706' :
                              t.service_type === 'pulsa' ? '#9333ea' : '#475569';
          
          const rowBg = idx % 2 === 0 ? '#ffffff' : '#f8fafc';
          
          const adminCols = isTagihanOnly ? '' : `
              <td style="padding: 10px 8px; text-align: right; border-bottom: 1px solid #e2e8f0; font-size: 13px; font-weight: 500; color: #059669;">${formatRp(t.admin_fee)}</td>
              <td style="padding: 10px 8px; text-align: right; border-bottom: 1px solid #e2e8f0; font-size: 13px; font-weight: 600; color: #0284c7;">${formatRp(t.total || t.total_bayar)}</td>
          `;
          
          tableRows += `
            <tr style="background: ${rowBg};">
              <td style="padding: 10px 8px; text-align: center; border-bottom: 1px solid #e2e8f0; font-size: 13px;">${rowNum}</td>
              <td style="padding: 10px 8px; border-bottom: 1px solid #e2e8f0;">
                <span style="display: inline-block; padding: 3px 10px; border-radius: 9999px; font-size: 11px; font-weight: 600; background: ${serviceBg}; color: ${serviceColor};">
                  ${serviceLabel}
                </span>
              </td>
              <td style="padding: 10px 8px; border-bottom: 1px solid #e2e8f0; font-family: monospace; font-size: 13px;">${t.customer_no || t.no_samb || '-'}</td>
              <td style="padding: 10px 8px; text-align: center; border-bottom: 1px solid #e2e8f0;">
                ${(t.service_type === 'pdam' || t.service_type === 'pdam_tebo') ? 
                  `<span style="display: inline-flex; align-items: center; justify-content: center; width: 26px; height: 26px; border-radius: 9999px; background: #e0f2fe; color: #0284c7; font-weight: 600; font-size: 13px;">${countMonths(t.periode_list, t.service_type)}</span>` 
                  : '<span style="color: #94a3b8;">-</span>'}
              </td>
              <td style="padding: 10px 8px; border-bottom: 1px solid #e2e8f0; font-size: 13px; max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${t.customer_name || t.nama || '-'}</td>
              <td style="padding: 10px 8px; text-align: right; border-bottom: 1px solid #e2e8f0; font-size: 13px; font-weight: 500; color: #ea580c;">${formatRp(t.amount || t.jumlah)}</td>
              ${adminCols}
              <td style="padding: 10px 8px; border-bottom: 1px solid #e2e8f0; font-size: 12px; color: #64748b;">${formatTgl(t.created_at)}</td>
              <td style="padding: 10px 8px; text-align: center; border-bottom: 1px solid #e2e8f0;">
                <span style="display: inline-block; padding: 3px 10px; border-radius: 9999px; font-size: 11px; font-weight: 600; background: ${t.status === 'completed' || t.status === 'confirmed' || t.status === 'success' ? '#d1fae5' : '#fef3c7'}; color: ${t.status === 'completed' || t.status === 'confirmed' || t.status === 'success' ? '#059669' : '#d97706'};">
                  ${t.status === 'completed' || t.status === 'confirmed' || t.status === 'success' ? 'Sukses' : t.status}
                </span>
              </td>
            </tr>
          `;
        });

        // Build table header
        const adminHeaders = isTagihanOnly ? '' : `
                <th style="padding: 12px 8px; text-align: right; color: white; font-weight: 600; font-size: 12px; text-transform: uppercase;">Total Admin</th>
                <th style="padding: 12px 8px; text-align: right; color: white; font-weight: 600; font-size: 12px; text-transform: uppercase;">Total Bayar</th>
        `;

        const tableHtml = `
          <table style="width: 100%; border-collapse: collapse; border-radius: 12px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
            <thead>
              <tr style="background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);">
                <th style="padding: 12px 8px; text-align: center; color: white; font-weight: 600; font-size: 12px; text-transform: uppercase;">No</th>
                <th style="padding: 12px 8px; text-align: left; color: white; font-weight: 600; font-size: 12px; text-transform: uppercase;">Layanan</th>
                <th style="padding: 12px 8px; text-align: left; color: white; font-weight: 600; font-size: 12px; text-transform: uppercase;">No. Pelanggan</th>
                <th style="padding: 12px 8px; text-align: center; color: white; font-weight: 600; font-size: 12px; text-transform: uppercase;">Bulan</th>
                <th style="padding: 12px 8px; text-align: left; color: white; font-weight: 600; font-size: 12px; text-transform: uppercase;">Nama</th>
                <th style="padding: 12px 8px; text-align: right; color: white; font-weight: 600; font-size: 12px; text-transform: uppercase;">Total Tagihan</th>
                ${adminHeaders}
                <th style="padding: 12px 8px; text-align: left; color: white; font-weight: 600; font-size: 12px; text-transform: uppercase;">Tanggal</th>
                <th style="padding: 12px 8px; text-align: center; color: white; font-weight: 600; font-size: 12px; text-transform: uppercase;">Status</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        `;

        // Footer on last page
        let footerHtml = '';
        if (page === totalPages - 1) {
          if (isTagihanOnly) {
            footerHtml = `
              <div style="margin-top: 16px; padding: 16px; background: linear-gradient(135deg, #fff7ed 0%, #fed7aa 100%); border-radius: 12px; display: flex; justify-content: flex-end; gap: 32px;">
                <div style="text-align: right;">
                  <span style="font-size: 12px; color: #9a3412;">Total Tagihan (Tanpa Admin)</span>
                  <p style="font-size: 20px; font-weight: 700; color: #c2410c; margin: 4px 0 0 0;">${formatRp(getTotalTagihan())}</p>
                </div>
              </div>
            `;
          } else {
            footerHtml = `
              <div style="margin-top: 16px; padding: 16px; background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); border-radius: 12px; display: flex; justify-content: flex-end; gap: 32px;">
                <div style="text-align: right;">
                  <span style="font-size: 12px; color: #64748b;">Total Tagihan</span>
                  <p style="font-size: 16px; font-weight: 700; color: #ea580c; margin: 4px 0 0 0;">${formatRp(getTotalTagihan())}</p>
                </div>
                <div style="text-align: right;">
                  <span style="font-size: 12px; color: #64748b;">Total Admin</span>
                  <p style="font-size: 16px; font-weight: 700; color: #059669; margin: 4px 0 0 0;">${formatRp(displaySummary.total_admin_fee)}</p>
                </div>
                <div style="text-align: right;">
                  <span style="font-size: 12px; color: #64748b;">Total Pendapatan</span>
                  <p style="font-size: 18px; font-weight: 700; color: #0284c7; margin: 4px 0 0 0;">${formatRp(getTotalPendapatan())}</p>
                </div>
              </div>
            `;
          }
        }

        container.innerHTML = headerHtml + tableHtml + footerHtml;
        document.body.appendChild(container);

        const canvas = await html2canvas(container, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false
        });

        document.body.removeChild(container);

        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        const imgWidth = pageWidth - 20;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        const xPos = 10;
        const yPos = 10;
        const maxHeight = pageHeight - 20;
        
        if (imgHeight > maxHeight) {
          const scaleFactor = maxHeight / imgHeight;
          pdf.addImage(imgData, 'JPEG', xPos, yPos, imgWidth * scaleFactor, maxHeight);
        } else {
          pdf.addImage(imgData, 'JPEG', xPos, yPos, imgWidth, imgHeight);
        }

        toast.loading(`Memproses halaman ${page + 1} dari ${totalPages}...`, { id: "pdf-export" });
      }

      const modeFileSuffix = isTagihanOnly ? '_tagihan_saja' : '';
      const filename = `laporan_${userRole}_${startDate || 'all'}_${endDate || 'all'}${modeFileSuffix}.pdf`;
      pdf.save(filename);
      toast.success("PDF berhasil di-download!", { id: "pdf-export" });

    } catch (error) {
      console.error("Export PDF error:", error);
      toast.error("Gagal export PDF", { id: "pdf-export" });
    } finally {
      setIsExportingPDF(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200/50">
        <nav className="container mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-600 hover:text-sky-600 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Kembali</span>
          </button>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-100 text-sky-700">
              <Wallet className="w-4 h-4" />
              <span className="font-medium text-sm">{userName}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${userRole === 'agen' ? 'bg-emerald-100 text-emerald-700' : userRole === 'pengawas' ? 'bg-purple-100 text-purple-700' : 'bg-sky-200 text-sky-800'}`}>
                {userRole}
              </span>
            </div>
          </div>
        </nav>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="text-2xl font-heading font-bold text-slate-800 mb-6 flex items-center gap-3">
            <FileText className="w-7 h-7 text-sky-600" />
            Laporan Transaksi Saya
          </h1>

          {/* Filter */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">
                    Tanggal Mulai
                  </label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="h-11"
                    data-testid="start-date-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">
                    Tanggal Akhir
                  </label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="h-11"
                    data-testid="end-date-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">
                    <Filter className="w-4 h-4 inline mr-1" />
                    Tampilkan
                  </label>
                  <select
                    value={showMode}
                    onChange={(e) => setShowMode(e.target.value)}
                    className="w-full h-11 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                    data-testid="show-mode-select"
                  >
                    <option value="all">Tagihan + Admin</option>
                    <option value="tagihan_only">Tagihan Saja</option>
                  </select>
                </div>
                <Button onClick={fetchReports} className="h-11 px-6 bg-sky-500 hover:bg-sky-600" data-testid="filter-btn">
                  Filter
                </Button>
                <Button 
                  variant="outline" 
                  onClick={exportExcel} 
                  className="h-11 px-6 gap-2"
                  data-testid="export-excel-btn"
                >
                  <Download className="w-4 h-4" />
                  Export Excel
                </Button>
              </div>
              <div className="mt-4 flex justify-end">
                <Button 
                  variant="outline" 
                  onClick={exportPDF}
                  disabled={isExportingPDF}
                  className="h-11 px-6 gap-2 bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100"
                  data-testid="export-pdf-btn"
                >
                  <FileImage className="w-4 h-4" />
                  {isExportingPDF ? 'Memproses...' : 'Export PDF'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          {reports && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card className="bg-gradient-to-br from-slate-50 to-white border-slate-200">
                <CardContent className="p-5">
                  <p className="text-sm text-slate-500 mb-1">Total Transaksi</p>
                  <p className="text-2xl font-heading font-bold text-slate-800">
                    {displaySummary.total_transactions}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-orange-50 to-white border-orange-200">
                <CardContent className="p-5">
                  <p className="text-sm text-orange-600 mb-1">Total Tagihan</p>
                  <p className="text-xl font-heading font-bold text-orange-600">
                    {formatCurrency(getTotalTagihan())}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">Tanpa biaya admin</p>
                </CardContent>
              </Card>
              {showMode === "all" && (
                <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
                  <CardContent className="p-5">
                    <p className="text-sm text-emerald-600 mb-1">Total Biaya Admin</p>
                    <p className="text-xl font-heading font-bold text-emerald-600">
                      {formatCurrency(displaySummary.total_admin_fee)}
                    </p>
                  </CardContent>
                </Card>
              )}
              <Card className={`bg-gradient-to-br from-sky-50 to-white border-sky-200 ${showMode === "tagihan_only" ? "lg:col-span-2" : ""}`}>
                <CardContent className="p-5">
                  <p className="text-sm text-sky-600 mb-1">
                    {showMode === "tagihan_only" ? "Total Tagihan" : "Total Pendapatan"}
                  </p>
                  <p className="text-xl font-heading font-bold text-sky-600">
                    {showMode === "tagihan_only" 
                      ? formatCurrency(getTotalTagihan())
                      : formatCurrency(getTotalPendapatan())
                    }
                  </p>
                  {showMode === "all" && (
                    <p className="text-xs text-slate-400 mt-1">Tagihan + Admin</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Admin Fee Breakdown Row - Only show when showMode is "all" */}
          {reports && showMode === "all" && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <Card className="bg-white border-l-4 border-l-violet-500">
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500 mb-1">Petugas</p>
                  <p className="text-lg font-heading font-bold text-violet-600">
                    {formatCurrency(displaySummary.total_petugas || 0)}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">Rp 1.000 × jumlah bulan</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500 mb-1">Sistem</p>
                  <p className="text-lg font-heading font-bold text-blue-600">
                    {formatCurrency(displaySummary.total_sistem || 0)}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">Rp 1.000 × jumlah bulan</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-l-4 border-l-amber-500">
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500 mb-1">Maintenance</p>
                  <p className="text-lg font-heading font-bold text-amber-600">
                    {formatCurrency(displaySummary.total_maintenance || 0)}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">Rp 500 × jumlah bulan</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Tab: DigiFlazz vs Lainnya */}
          {allTxns.length > 0 && (
            <div className="flex gap-2 mb-4" data-testid="report-tabs">
              {[["digiflazz", "DigiFlazz"], ["lainnya", "Lainnya"]].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  data-testid={`tab-${key}`}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                    activeTab === key
                      ? "bg-sky-600 text-white shadow"
                      : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* Transactions Table */}
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner" />
            </div>
          ) : reports && displayTxns.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-heading font-semibold text-slate-800 mb-2">
                  {startDate || endDate ? "Tidak Ada Transaksi" : "Pilih Rentang Tanggal"}
                </h3>
                <p className="text-slate-600">
                  {startDate || endDate 
                    ? "Tidak ada transaksi pada periode ini" 
                    : "Pilih tanggal untuk melihat detail transaksi"}
                </p>
              </CardContent>
            </Card>
          ) : reports && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <FileText className="w-5 h-5 text-sky-600" />
                  Detail Pembayaran
                  {showMode === "tagihan_only" && (
                    <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full font-medium">
                      Mode: Tagihan Saja
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="reports-table">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="text-center p-3 font-semibold text-slate-600">No</th>
                        <th className="text-left p-3 font-semibold text-slate-600">Layanan</th>
                        <th className="text-left p-3 font-semibold text-slate-600">No. Pelanggan</th>
                        <th className="text-center p-3 font-semibold text-slate-600">Bulan</th>
                        <th className="text-left p-3 font-semibold text-slate-600">Nama</th>
                        <th className="text-right p-3 font-semibold text-slate-600">Total Tagihan</th>
                        {showMode === "all" && <th className="text-right p-3 font-semibold text-slate-600">Total Admin</th>}
                        {showMode === "all" && <th className="text-right p-3 font-semibold text-slate-600">Total Bayar</th>}
                        <th className="text-center p-3 font-semibold text-slate-600">Metode</th>
                        <th className="text-left p-3 font-semibold text-slate-600">Tanggal</th>
                        <th className="text-center p-3 font-semibold text-slate-600">Status</th>
                        <th className="text-center p-3 font-semibold text-slate-600">Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayTxns.map((txn, index) => (
                        <tr key={txn.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-3 text-center text-slate-500">{index + 1}</td>
                          <td className="p-3">
                            <span className={`text-xs px-2 py-1 rounded-full font-medium
                              ${txn.service_type === 'pdam' ? 'bg-blue-100 text-blue-700' : ''}
                              ${txn.service_type === 'pdam_tebo' ? 'bg-cyan-100 text-cyan-700' : ''}
                              ${txn.service_type === 'token_listrik' ? 'bg-amber-100 text-amber-700' : ''}
                              ${txn.service_type === 'pulsa' ? 'bg-purple-100 text-purple-700' : ''}
                            `}>
                              {getServiceLabel(txn.service_type)}
                            </span>
                          </td>
                          <td className="p-3 font-mono text-sm">{txn.customer_no || txn.no_samb || '-'}</td>
                          <td className="p-3 text-center">
                            {(txn.service_type === 'pdam' || txn.service_type === 'pdam_tebo') ? (
                              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-sky-100 text-sky-700 font-semibold text-sm">
                                {countMonths(txn.periode_list, txn.service_type)}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="p-3 max-w-[150px] truncate">{txn.customer_name || txn.nama || '-'}</td>
                          <td className="p-3 text-right font-medium text-orange-600">{formatCurrency(txn.amount || txn.jumlah)}</td>
                          {showMode === "all" && (
                            <td className="p-3 text-right font-medium text-emerald-600">{formatCurrency(txn.admin_fee)}</td>
                          )}
                          {showMode === "all" && (
                            <td className="p-3 text-right font-medium text-sky-600">{formatCurrency(txn.total || txn.total_bayar)}</td>
                          )}
                          <td className="p-3 text-center">
                            {txn.payment_method?.toLowerCase() === 'cash' ? (
                              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-700">Cash</span>
                            ) : txn.payment_method?.toLowerCase() === 'transfer' ? (
                              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700">Transfer</span>
                            ) : (
                              <span className="text-xs text-slate-400">-</span>
                            )}
                          </td>
                          <td className="p-3 text-sm text-slate-500">{formatDate(txn.created_at)}</td>
                          <td className="p-3 text-center">
                            <span className={`text-xs px-2 py-1 rounded-full font-medium
                              ${txn.status === 'completed' || txn.status === 'confirmed' || txn.status === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}
                            `}>
                              {txn.status === 'completed' || txn.status === 'confirmed' || txn.status === 'success' ? 'Sukses' : txn.status}
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setViewingDetail(txn)}
                              className="gap-1 text-sky-600 border-sky-200 hover:bg-sky-50"
                              data-testid={`detail-btn-${txn.id}`}
                            >
                              <Eye className="w-4 h-4" />
                              Detail
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50 font-semibold">
                      <tr>
                        <td colSpan="5" className="p-3 text-right text-slate-600">TOTAL:</td>
                        <td className="p-3 text-right text-orange-600">{formatCurrency(getTotalTagihan())}</td>
                        {showMode === "all" && (
                          <td className="p-3 text-right text-emerald-600">{formatCurrency(displaySummary.total_admin_fee)}</td>
                        )}
                        {showMode === "all" && (
                          <td className="p-3 text-right text-sky-600">{formatCurrency(getTotalPendapatan())}</td>
                        )}
                        <td colSpan={showMode === "all" ? "4" : "4"}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </motion.div>
      </main>

      {/* Detail Modal */}
      {viewingDetail && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setViewingDetail(null)}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative max-w-lg w-full bg-white rounded-2xl overflow-hidden max-h-[90vh] overflow-y-auto" 
            onClick={e => e.stopPropagation()}
          >
            <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-sky-50 sticky top-0">
              <h3 className="font-heading font-semibold text-slate-800">Detail Transaksi</h3>
              <button
                onClick={() => setViewingDetail(null)}
                className="w-8 h-8 rounded-full hover:bg-slate-200 flex items-center justify-center"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Customer Info */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="text-sm font-medium text-slate-500 mb-3">Informasi Pelanggan</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Layanan</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      viewingDetail.service_type === 'pdam' ? 'bg-blue-100 text-blue-700' :
                      viewingDetail.service_type === 'pdam_tebo' ? 'bg-cyan-100 text-cyan-700' :
                      viewingDetail.service_type === 'token_listrik' ? 'bg-yellow-100 text-yellow-700' :
                      viewingDetail.service_type === 'pulsa' ? 'bg-purple-100 text-purple-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {getServiceLabel(viewingDetail.service_type)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">No. Pelanggan</span>
                    <span className="font-mono font-medium">{viewingDetail.customer_no || viewingDetail.no_samb}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Nama</span>
                    <span className="font-medium">{viewingDetail.customer_name || viewingDetail.nama}</span>
                  </div>
                  {viewingDetail.periode_list && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Periode</span>
                      <span className="font-medium text-right">{viewingDetail.periode_list}</span>
                    </div>
                  )}
                  {viewingDetail.product_name && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Produk</span>
                      <span className="font-medium text-right text-xs max-w-[60%]">{viewingDetail.product_name}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Bill details breakdown for batch payments */}
              {viewingDetail.bill_details?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-slate-600">Detail Per Periode:</h4>
                  {viewingDetail.bill_details.map((bill, idx) => (
                    <div key={idx} className="bg-sky-50 rounded-lg p-3 text-sm border border-sky-100">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-semibold text-slate-800">{bill.periode_text || bill.bulan}</span>
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">LUNAS</span>
                      </div>
                      <div className="flex justify-between text-slate-600">
                        <span>Total Tagihan</span>
                        <span className="font-medium">{formatCurrency(bill.total_tagihan || bill.nilai_tagihan)}</span>
                      </div>
                      <div className="flex justify-between text-cyan-600">
                        <span>Biaya Admin</span>
                        <span className="font-medium">{formatCurrency(bill.admin_fee || viewingDetail.admin_fee / (viewingDetail.bill_details?.length || 1))}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Payment Info */}
              <div className="bg-emerald-50 rounded-xl p-4">
                <h4 className="text-sm font-medium text-emerald-700 mb-3">Informasi Pembayaran</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-emerald-600">Tagihan</span>
                    <span className="font-medium">{formatCurrency(viewingDetail.amount || viewingDetail.jumlah)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-emerald-600">Biaya Admin</span>
                    <span className="font-medium">{formatCurrency(viewingDetail.admin_fee)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-emerald-200">
                    <span className="text-emerald-700 font-semibold">Total Bayar</span>
                    <span className="font-bold text-emerald-700">{formatCurrency(viewingDetail.total || viewingDetail.total_bayar)}</span>
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-slate-500 text-xs mb-1">Tanggal Transaksi</p>
                  <p className="font-medium">{formatDate(viewingDetail.created_at)}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-slate-500 text-xs mb-1">Status</p>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    viewingDetail.status === 'completed' || viewingDetail.status === 'confirmed' || viewingDetail.status === 'success'
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {viewingDetail.status === 'completed' || viewingDetail.status === 'confirmed' || viewingDetail.status === 'success' ? 'Sukses' : viewingDetail.status}
                  </span>
                </div>
              </div>

              {/* SN / Token for digital products */}
              {viewingDetail.sn && (
                <div className="bg-amber-50 rounded-xl p-4">
                  <p className="text-xs text-amber-600 mb-1">SN / Token</p>
                  <p className="font-mono text-sm font-medium text-amber-800 break-all">{viewingDetail.sn}</p>
                </div>
              )}

              {/* Payment Proof if exists */}
              {viewingDetail.payment_proof && (
                <div>
                  <h4 className="text-sm font-medium text-slate-600 mb-3 flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Bukti Pembayaran
                  </h4>
                  <div 
                    className="cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => setViewingProof(viewingDetail.payment_proof)}
                  >
                    <img 
                      src={viewingDetail.payment_proof} 
                      alt="Bukti Pembayaran" 
                      className="w-full h-48 object-cover rounded-xl border border-slate-200"
                    />
                    <p className="text-xs text-center text-slate-500 mt-2">Klik untuk memperbesar</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Full Size Image Modal */}
      {viewingProof && (
        <div className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4" onClick={() => setViewingProof(null)}>
          <button
            onClick={() => setViewingProof(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img 
            src={viewingProof} 
            alt="Bukti Pembayaran" 
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
